import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ThemeProvider, useTheme, SectionCard,
  Topbar, LoadingScreen, EmptyState, DeadlineChip, StatusBadge, UserProfileCard, AnimatedStatCard
} from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

function PendingCard({ c, navigate, isSuper, theme }) {
  return (
    <div className="ur-card-hover" onClick={() => navigate('/admin/verify/' + c.id)}
      style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer', marginBottom: 8,
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
        borderLeft: '3px solid ' + (c.is_overdue ? COLORS.red : COLORS.amber) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {c.is_overdue && <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.red,
          flexShrink: 0, boxShadow: '0 0 6px ' + COLORS.red }} />}
        <div style={{ fontSize: 13, fontWeight: 600, color: c.is_overdue ? '#fca5a5' : '#fff' }}>{c.title}</div>
      </div>
      <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6 }}>
        {c.student_name}{isSuper && c.department ? ' - ' + c.department : ''}
      </div>
      <DeadlineChip seconds={c.time_left_seconds} />
    </div>
  );
}

function ColHeader({ label, count }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 10,
      textTransform: 'uppercase', letterSpacing: 1 }}>
      {label} ({count})
    </div>
  );
}

function AdminDashboardInner() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState(null);
  const [communityData, setCommunityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    const [statsRes] = await Promise.allSettled([api.get('/admin/dashboard-stats')]);
    if (statsRes.status === 'fulfilled') {
      setStats(statsRes.value.data);
      setError('');
    } else {
      setError(statsRes.reason?.response?.data?.error || 'Failed to load dashboard data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    api.get('/community/')
      .then(res => setCommunityData(res.data))
      .catch(() => {});
  }, []);

  if (loading) return <LoadingScreen />;

  const isSuper = stats?.is_super_admin;
  const pending = stats?.pending_list || [];
  const studentPending = pending.filter(c => c.submitter_role === 'student');
  const facultyPending = pending.filter(c => c.submitter_role === 'faculty');

  const complaintCards = [
    { label: 'Total',    value: stats?.total_complaints    || 0, color: COLORS.purple },
    { label: 'Pending',  value: stats?.pending_complaints  || 0, color: COLORS.amber, onClick: () => navigate('/admin/pending-complaints') },
    { label: 'Verified', value: stats?.verified_complaints || 0, color: COLORS.blue, onClick: () => navigate('/admin/verified-complaints') },
    { label: 'Resolved', value: stats?.resolved_complaints || 0, color: COLORS.green },
    { label: 'Rejected', value: stats?.rejected_complaints || 0, color: COLORS.red },
  ];

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)}
          mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarCollapsed ? 0 : 248) }}>
          <Topbar
            title={isSuper ? 'University Dashboard' : 'Department Dashboard'}
            subtitle={(stats?.department || user?.department || '') + ' - Admin Panel'}
            onToggleSidebar={() => setSidebarCollapsed(c => !c)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={fetchStats}>
                Refresh
              </button>
            }
          />
          <div style={s.content}>
            {error && <div style={s.alertErr}>{error}</div>}
            <UserProfileCard user={user} />

            {/* Complaints section */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
                {isSuper ? '\uD83D\uDCCB University Complaints' : `\uD83D\uDCCB ${stats?.department || user?.department || 'Department'} Complaints`}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
                {complaintCards.map(({ label, value, color, icon, onClick }, i) => (
                  <AnimatedStatCard key={label} label={label} value={value} color={color} onClick={onClick} delay={`${0.05 + i * 0.06}s`} />
                ))}
              </div>
            </div>

            {/* Pending complaints */}
            <div style={{ marginBottom: 16 }}>
              <SectionCard
                title="Pending Complaints"
                subtitle="Sorted by deadline"
                delay="0.3s"
                action={
                  <button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/admin/pending-complaints')}>
                    View All
                  </button>
                }
              >
                {!pending.length
                  ? <EmptyState text="No pending complaints" sub="All caught up" />
                  : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 16 }}>
                      <div>
                        <ColHeader label="Student Complaints" count={studentPending.length} />
                        {!studentPending.length
                          ? <EmptyState text="No student complaints" sub="" />
                          : studentPending.slice(0, 4).map(c => (
                            <PendingCard key={c.id} c={c} navigate={navigate} isSuper={isSuper} theme={theme} />
                          ))
                        }
                      </div>
                      <div>
                        <ColHeader label="Faculty Complaints" count={facultyPending.length} />
                        {!facultyPending.length
                          ? <EmptyState text="No faculty complaints" sub="" />
                          : facultyPending.slice(0, 4).map(c => (
                            <PendingCard key={c.id} c={c} navigate={navigate} isSuper={isSuper} theme={theme} />
                          ))
                        }
                      </div>
                    </div>
                  )
                }
              </SectionCard>
            </div>

            {communityData && (
              <div style={{ marginBottom: 16 }}>
                <SectionCard
                  title="Community Board"
                  subtitle={isSuper ? 'University-wide complaints' : (stats?.department || '') + ' department complaints'}
                  delay="0.38s"
                  action={
                    <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/community')}>
                      View All
                    </button>
                  }
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 16 }}>
                    <div>
                      <ColHeader label="Student Complaints" count={(communityData.student_complaints || []).length} />
                      {!(communityData.student_complaints || []).length
                        ? <EmptyState text="No student complaints" sub="" />
                        : (communityData.student_complaints || []).slice(0, 3).map(c => (
                          <div key={c.id} className="ur-card-hover" onClick={() => navigate('/complaints/' + c.id)}
                            style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer', marginBottom: 8,
                              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                              borderLeft: '3px solid ' + COLORS.cyan }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 3 }}>{c.title}</div>
                            <StatusBadge status={c.status} />
                          </div>
                        ))
                      }
                    </div>
                    <div>
                      <ColHeader label="Faculty Complaints" count={(communityData.faculty_complaints || []).length} />
                      {!(communityData.faculty_complaints || []).length
                        ? <EmptyState text="No faculty complaints" sub="" />
                        : (communityData.faculty_complaints || []).slice(0, 3).map(c => (
                          <div key={c.id} className="ur-card-hover" onClick={() => navigate('/complaints/' + c.id)}
                            style={{ padding: '10px 12px', borderRadius: 10, cursor: 'pointer', marginBottom: 8,
                              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                              borderLeft: '3px solid ' + COLORS.amber }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 3 }}>{c.title}</div>
                            <StatusBadge status={c.status} />
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </SectionCard>
              </div>
            )}

            <SectionCard title="Quick Actions" delay="0.4s">
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button className="ur-btn-hover" style={s.btnPrimary}   onClick={() => navigate('/admin/pending-complaints')}>Review Pending</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/admin/verified-complaints')}>Verified Complaints</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/community')}>Community Board</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/profile')}>My Profile</button>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <ThemeProvider>
      <AdminDashboardInner />
    </ThemeProvider>
  );
}
